import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import {
  Play,
  Pause,
  FileText,
  Download,
  ExternalLink,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Volume2,
  X,
} from "lucide-react";
import { api, API } from "@/lib/apiClient";
import { toast } from "sonner";
import { splitSentences, buildOffsets, indexForProgress } from "@/lib/ttsSync";

/**
 * Renders an uploaded PDF inline (browser-native PDF viewer) plus a row of
 * inline YouTube players for every link Brimi auto-detected inside the PDF.
 *
 * Also includes a page-by-page "Read aloud" (TTS) narration bar with a
 * "Now reading" caption that highlights the current sentence in sync with
 * audio playback, and a Cancel button that closes the narration UI without
 * touching the PDF below.
 */
export const PdfViewer = ({
  topicId,
  pdfUrl,
  fileName,
  sizeKb,
  pageCount,
  youtubeLinks = [],
  accent,
}) => {
  // Deduplicate by video_id (defensive — backend already dedups)
  const videos = useMemo(() => {
    const seen = new Set();
    return (youtubeLinks || []).filter((v) => {
      if (!v?.video_id || seen.has(v.video_id)) return false;
      seen.add(v.video_id);
      return true;
    });
  }, [youtubeLinks]);

  // ----- Read aloud (TTS) per page -----
  const totalPages = Math.max(pageCount || 0, 0);
  const [page, setPage] = useState(1); // 1-indexed for UI
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [slowGen, setSlowGen] = useState(false);
  const [pageText, setPageText] = useState("");
  const [sentenceIdx, setSentenceIdx] = useState(-1);
  // True after the user has started narration for the first time — shows the caption card.
  const [active, setActive] = useState(false);

  const sentences = useMemo(() => splitSentences(pageText), [pageText]);
  const { offsets } = useMemo(() => buildOffsets(sentences), [sentences]);

  const audioSrc = useMemo(() => {
    if (!topicId || totalPages === 0) return null;
    const token = localStorage.getItem("brimi_token");
    if (!token) return null;
    return `${API}/materials/${topicId}/pdf/page/${page - 1}/audio?auth=${encodeURIComponent(token)}`;
  }, [topicId, page, totalPages]);

  // Fetch page text whenever caption is active and page changes (used for "Now reading").
  useEffect(() => {
    if (!active || !topicId || totalPages === 0) return;
    let cancelled = false;
    setPageText("");
    setSentenceIdx(-1);
    api
      .get(`/materials/${topicId}/pdf/page/${page - 1}/text`)
      .then(({ data }) => {
        if (!cancelled) setPageText(data?.text || "");
      })
      .catch(() => {
        if (!cancelled) setPageText("");
      });
    return () => {
      cancelled = true;
    };
  }, [active, topicId, page, totalPages]);

  // Reset playback when page changes
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    setPlaying(false);
    setAudioLoading(false);
    setSlowGen(false);
    setSentenceIdx(-1);
  }, [page]);

  // Slow-generation hint after 2s
  useEffect(() => {
    if (!audioLoading) {
      setSlowGen(false);
      return;
    }
    const t = setTimeout(() => setSlowGen(true), 2000);
    return () => clearTimeout(t);
  }, [audioLoading]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      setActive(true);
      setAudioLoading(true);
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          toast.error("Couldn't play audio. Try again.");
          setAudioLoading(false);
        });
      }
    } else {
      el.pause();
    }
  }, []);

  const handleCancel = () => {
    const el = audioRef.current;
    if (el) {
      try {
        el.pause();
        el.currentTime = 0;
      } catch (_e) { /* ignore */ }
    }
    setPlaying(false);
    setAudioLoading(false);
    setSlowGen(false);
    setSentenceIdx(-1);
    setPageText("");
    setActive(false);
  };

  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el || !el.duration || !Number.isFinite(el.duration)) return;
    const progress = el.currentTime / el.duration;
    const idx = indexForProgress(offsets, progress);
    setSentenceIdx((prev) => (prev === idx ? prev : idx));
  };

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages || p, p + 1));

  const currentSentence =
    sentenceIdx >= 0 && sentenceIdx < sentences.length ? sentences[sentenceIdx] : "";

  return (
    <div className="space-y-6">
      {/* PDF embed */}
      <div className="tactile-card p-3 sm:p-4" data-testid="pdf-viewer-card">
        <div className="flex items-center justify-between gap-3 mb-3 px-1">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 shrink-0" style={{ color: accent }} />
            <div className="text-xs sm:text-sm font-bold truncate">
              {fileName || "Chapter notes"}
            </div>
          </div>
          <div className="text-xs text-muted-foreground font-semibold whitespace-nowrap">
            {pageCount ? `${pageCount} pages` : ""}
            {pageCount && sizeKb ? " · " : ""}
            {sizeKb ? `${sizeKb} KB` : ""}
          </div>
        </div>
        <div className="w-full rounded-2xl overflow-hidden border border-border bg-muted">
          <iframe
            src={pdfUrl}
            title={fileName || "Chapter PDF"}
            data-testid="pdf-iframe"
            className="w-full h-[70vh] sm:h-[78vh] block"
          />
        </div>

        {/* Read aloud narration bar (only when we know page count) */}
        {totalPages > 0 && topicId && (
          <div
            data-testid="pdf-narration-bar"
            className="mt-3 rounded-2xl bg-muted/60 border border-border overflow-hidden"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  data-testid="pdf-play-audio-btn"
                  onClick={togglePlay}
                  disabled={!audioSrc}
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white disabled:opacity-40"
                  title={playing ? "Pause narration" : "Play narration"}
                >
                  {audioLoading && !playing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : playing ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-0.5" />
                  )}
                </button>
                {active && (
                  <button
                    data-testid="pdf-cancel-audio-btn"
                    onClick={handleCancel}
                    title="Stop & close narration"
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-destructive/10 hover:bg-destructive/20 text-destructive"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <div className="min-w-0">
                  <div className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    <Volume2 className="w-3 h-3" /> Read aloud
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {audioLoading
                      ? slowGen
                        ? "Generating narration… (cached after this)"
                        : "Loading…"
                      : playing
                        ? `Reading page ${page}…`
                        : `Tap play to hear page ${page}`}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid="pdf-page-prev"
                  onClick={goPrev}
                  disabled={page <= 1}
                  className="pill-btn bg-foreground/5 hover:bg-foreground/10 px-3 py-1.5 text-xs disabled:opacity-40"
                  title="Previous page"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <input
                  data-testid="pdf-page-input"
                  type="number"
                  min={1}
                  max={totalPages}
                  value={page}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) {
                      setPage(Math.min(totalPages, Math.max(1, v)));
                    }
                  }}
                  className="w-14 text-center text-sm font-bold rounded-md border border-border bg-background py-1"
                />
                <span className="text-xs text-muted-foreground font-semibold">
                  / {totalPages}
                </span>
                <button
                  data-testid="pdf-page-next"
                  onClick={goNext}
                  disabled={page >= totalPages}
                  className="pill-btn bg-foreground/5 hover:bg-foreground/10 px-3 py-1.5 text-xs disabled:opacity-40"
                  title="Next page"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              <audio
                ref={audioRef}
                src={audioSrc || undefined}
                preload="none"
                onPlay={() => {
                  setPlaying(true);
                  setAudioLoading(false);
                  setActive(true);
                }}
                onPause={() => setPlaying(false)}
                onEnded={() => {
                  setPlaying(false);
                  setAudioLoading(false);
                  setSentenceIdx(-1);
                }}
                onError={() => {
                  setPlaying(false);
                  setAudioLoading(false);
                  toast.error("Couldn't load narration for this page.");
                }}
                onWaiting={() => setAudioLoading(true)}
                onCanPlay={() => setAudioLoading(false)}
                onTimeUpdate={onTimeUpdate}
                className="hidden"
                data-testid="pdf-narration-audio"
              />
            </div>

            {/* Now-reading caption — only after user starts narration. */}
            {active && sentences.length > 0 && (
              <div
                data-testid="pdf-narration-caption"
                className="border-t border-border bg-[hsl(var(--brimi-blue))]/8 px-4 py-3"
              >
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--brimi-blue))] mb-1 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <Volume2 className="w-3 h-3" />
                    Now reading{!playing && audioRef.current?.currentTime > 0 ? " (paused)" : ""}
                  </span>
                  <span className="font-mono text-[10px] text-foreground/50">
                    {sentenceIdx >= 0 ? sentenceIdx + 1 : 0}/{sentences.length}
                  </span>
                </div>
                <div className="text-sm sm:text-base leading-snug font-medium text-foreground">
                  {currentSentence || (audioLoading ? "Loading…" : sentences[0])}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex justify-end">
          <a
            href={pdfUrl}
            download={fileName || "notes.pdf"}
            data-testid="pdf-download-link"
            className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </a>
        </div>
      </div>

      {/* Inline YouTube players */}
      {videos.length > 0 && (
        <div data-testid="pdf-videos-section">
          <div className="flex items-center gap-2 mb-3">
            <Play className="w-4 h-4" style={{ color: accent }} />
            <h3 className="text-lg font-black">
              Video explanations from your notes
            </h3>
            <span className="text-xs text-muted-foreground font-semibold">
              ({videos.length} {videos.length === 1 ? "video" : "videos"})
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {videos.map((v) => (
              <div
                key={v.video_id}
                data-testid={`yt-card-${v.video_id}`}
                className="tactile-card p-3"
              >
                <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${v.video_id}`}
                    title={`YouTube video ${v.video_id}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Page {v.page}</span>
                  <a
                    href={`https://www.youtube.com/watch?v=${v.video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`yt-external-${v.video_id}`}
                    className="inline-flex items-center gap-1 font-semibold hover:text-foreground"
                  >
                    Open on YouTube
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
